import React from "react";
import {
	alpha,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import MiscellaneousServicesTwoToneIcon from "@mui/icons-material/MiscellaneousServicesTwoTone";
import type { IOfflineService } from "../../type/offlineExhibitorType";

interface Props {
	services?: IOfflineService[];
}

const formatPriceLabel = (price: string, priceType?: string) => {
	if (!price) return "N/A";
	const trimmed = price.trim();
	if (priceType === "negotiable" || trimmed.toLowerCase() === "negotiable") return "Negotiable";
	if (priceType === "range" || trimmed.includes("-")) {
		const parts = trimmed.split("-");
		if (parts.length === 2 && (parts[0]?.trim() || parts[1]?.trim())) {
			const minPart = parts[0]?.trim();
			const maxPart = parts[1]?.trim();
			if (minPart && maxPart) {
				return `BDT ${minPart} - ${maxPart}`;
			}
			return `BDT ${trimmed}`;
		}
	}
	if (trimmed.startsWith("BDT")) return trimmed;
	return `BDT ${trimmed}`;
};

const ExhibitorServicesTab: React.FC<Props> = ({ services }) => {
	return (
		<TableContainer>
			<Table>
				<TableHead>
					<TableRow
						sx={{
							bgcolor: (theme) =>
								alpha(theme.palette.primary.main, 0.04),
						}}
					>
						<TableCell sx={{ fontWeight: 700, width: 70, color: "text.primary" }}>
							SL
						</TableCell>
						<TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
							Service Name
						</TableCell>
						<TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
							Price (BDT)
						</TableCell>
						<TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
							Description
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{services && services.length > 0 ? (
						services.map((srv, idx) => {
							const isNegotiable =
								srv.price_type === "negotiable" ||
								srv.price?.trim().toLowerCase() === "negotiable";

							return (
								<TableRow
									key={srv.id || idx}
									hover
									sx={{
										"&:hover": {
											bgcolor: (theme) =>
												alpha(theme.palette.primary.main, 0.02),
										},
									}}
								>
									<TableCell
										sx={{
											color: "text.secondary",
											fontWeight: 600,
										}}
									>
										{idx + 1}
									</TableCell>
									<TableCell>
										<Typography
											variant="body2"
											fontWeight={600}
											color="text.primary"
										>
											{srv.service_name}
										</Typography>
									</TableCell>
									<TableCell>
										<Chip
											label={formatPriceLabel(srv.price, srv.price_type)}
											size="small"
											color={isNegotiable ? "info" : "success"}
											variant="filled"
											sx={{
												fontWeight: 700,
												borderRadius: 1.5,
											}}
										/>
									</TableCell>
									<TableCell sx={{ color: "text.secondary" }}>
										{srv.service_description || "-"}
									</TableCell>
								</TableRow>
							);
						})
					) : (
						<TableRow>
							<TableCell colSpan={4} align="center" sx={{ py: 6 }}>
								<MiscellaneousServicesTwoToneIcon
									sx={{
										fontSize: 48,
										color: "text.disabled",
										mb: 1,
									}}
								/>
								<Typography
									variant="h5"
									color="text.secondary"
									fontWeight={600}
								>
									No Services Found
								</Typography>
								<Typography variant="body2" color="text.disabled">
									No services have been registered for this exhibitor yet.
								</Typography>
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export default ExhibitorServicesTab;
